import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PrincipalType } from '@fleet/shared';
import { usersAdminApi } from '../../api/users';
import { Button } from '../../ui/Button';
import { ErrorNotice } from '../../ui/ErrorNotice';
import { Modal } from '../../ui/Modal';
import { PrincipalPicker } from '../permissions/PrincipalPicker';
import type { DirectoryPrincipal } from '../../api/permissions';

/**
 * Eine Person aus dem Verzeichnis anlegen, ohne dass sie sich angemeldet hat.
 *
 * Bis hierher entstand ein Nutzer ausschließlich beim ersten Login. Für jemanden,
 * der neu anfängt, hieß das: erst anmelden lassen, dann einrichten — und bis
 * dahin ließ sich weder für ihn buchen noch ihm etwas erlauben.
 *
 * Gesendet wird **nur die Objekt-ID**. Anzeigename und Mailadresse holt der
 * Server aus Graph; was hier angezeigt wird, ist nur die Bestätigung für die
 * bedienende Person, nicht die Grundlage des Datensatzes.
 */
export function ProvisionUserDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<DirectoryPrincipal | null>(null);

  const provision = useMutation({
    mutationFn: (entraObjectId: string) => usersAdminApi.provision(entraObjectId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      // Auch die schmale Personenliste: Die neue Person soll sofort für eine
      // Buchung auswählbar sein, nicht erst nach einem Neuladen.
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  return (
    <Modal
      title="Nutzer anlegen"
      description="Person im Verzeichnis suchen. Sie kann danach buchen und Berechtigungen bekommen, auch bevor sie sich zum ersten Mal anmeldet."
      onClose={onClose}
    >
      <div className="space-y-4">
        <PrincipalPicker only={PrincipalType.USER} onSelect={setSelected} />

        {selected && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="font-medium">{selected.displayName}</div>
            <div className="text-xs text-slate-500">{selected.secondary ?? 'ohne Mailadresse'}</div>
          </div>
        )}

        {provision.isError && <ErrorNotice error={provision.error} />}

        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            disabled={!selected || provision.isPending}
            onClick={() => {
              if (selected) provision.mutate(selected.entraObjectId);
            }}
          >
            Anlegen
          </Button>
        </div>
      </div>
    </Modal>
  );
}
